import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  AdvancedFeasibilityFilters,
  AidaEligibility,
  BasicToken,
  LaunchPricingSummary,
  LaunchToggleField,
  RespondentFiltersRequest,
  RespondentListOrigin,
  SurveyLaunchFormState,
  SurveyUrlsForm,
} from '@ps/survey-utils';
import { SurveyStatus } from '@ps/survey-kernel';
import { Nullable } from '@ps/utils';

// Extend with a new union member for any future survey-launch activity-log
// trigger — no new action needed, see `Log Survey Launch Activity` below.
export type SurveyLaunchActivityLogTrigger =
  | {
      readonly kind: 'launchToggle';
      readonly field: LaunchToggleField;
      readonly enabled: boolean;
    }
  | { readonly kind: 'aidaToggled'; readonly enabled: boolean }
  | { readonly kind: 'uniqueBuyerEntryLinksUploaded' }
  | { readonly kind: 'uniqueBuyerEntryLinksCleared' }
  | {
      readonly kind: 'emailNotificationChanged';
      readonly from: string;
      readonly to: string;
    }
  | {
      readonly kind: 'selectedProjectManager';
      readonly from: string;
      readonly to: string;
    }
  | { readonly kind: 'includeExcludeListUploaded' }
  | {
      readonly kind: 'externalSurveyIdChanged';
      readonly externalSurveyId: string;
    }
  | { readonly kind: 'surveyGroupingUpdated' }
  | {
      readonly kind: 'dateRangeFilterApplied';
      readonly from: string;
      readonly to: string;
    };

export const SurveyCreationLaunchActions = createActionGroup({
  source: 'Survey Creation Launch',
  events: {
    Init: props<{ surveyId: Nullable<number> }>(),
    'Update Form': props<{ form: SurveyLaunchFormState }>(),
    'Patch Survey Urls': props<{ surveyUrls: SurveyUrlsForm }>(),
    'Download Entry Links Template': emptyProps(),
    'Download Respondent List Template': emptyProps(),
    'Upload Respondent List': props<{ file: File }>(),
    'Load Respondent List': props<{ inclExcl: 0 | 1 }>(),
    'Clear Respondent List': emptyProps(),
    'Toggle Include Exclude Status': props<{ enabled: boolean }>(),
    // Date range / survey grouping edited — resolves the matching surveys and
    // PSIDs, then commits them as the survey's advanced filters.
    'Resolve Respondent Filters': props<RespondentFiltersRequest>(),
    // Date range cleared — the grouping reverts to the survey's saved one and
    // the generated PSIDs are dropped, with no lookup (legacy cancelFilterDateRange).
    'Restore Saved Grouping': props<{ savedSurveyIds: number[] }>(),
    // Respondent statuses picked on the launch page — committed as the
    // survey's advanced filters, the source the save payload reads.
    'Update Respondent Statuses': props<{ statuses: number[] }>(),

    // No payload — the effect reads the current form values and the
    // survey's own baseline straight from the store.
    'Request Test Entry Link': emptyProps(),
    'Patch Test Urls': props<{ liveUrl: string; testUrl: string }>(),
    // The billing id input lost focus — check it against invoiced surveys.
    // Carries the live typed value since the debounced form→state sync may
    // not have committed it to the store yet.
    'Check Billing Code Already Used': props<{ billingId: string }>(),

    // Link Security toggle / token edit — persisted immediately, as legacy
    // did, because the server generates the token when enabled without one.
    'Update Basic Token': props<{ enable: boolean; value: string }>(),

    // Activity Log — single generic trigger, see `SurveyLaunchActivityLogTrigger`.
    'Log Survey Launch Activity': props<{
      surveyId: number;
      trigger: SurveyLaunchActivityLogTrigger;
    }>(),

    // AI section shown on a Draft — ask the backend whether AIDA can run.
    'Load Aida Eligibility': props<{ surveyId: number }>(),

    // Launch confirmation drawer opened — fetch what it shows.
    'Load Launch Pricing': emptyProps(),
    // Save, then move the survey to `status` (LIVE, or SCHEDULED for a
    // scheduled auto-launch). The message is the pricing the buyer accepted,
    // logged before the status change; '' when no pricing was confirmed.
    'Launch Survey': props<{
      status: SurveyStatus;
      pricingConfirmationMessage: string;
    }>(),
  },
});

// Events the TEST-button effects dispatch in response to their own outcomes.
// Internal to data-access — the actions facade forwards commands only.
export const SurveyCreationLaunchApiActions = createActionGroup({
  source: 'Survey Creation Launch API',
  events: {
    'Upload Respondent List Success': props<{ items: string[] }>(),
    'Upload Respondent List Failure': props<{ error: string }>(),
    'Load Respondent List Success': props<{
      items: string[];
      origin: RespondentListOrigin;
    }>(),
    'Load Respondent List Failure': props<{ error: string }>(),
    'Resolve Respondent Filters Success': props<{
      filters: AdvancedFeasibilityFilters;
    }>(),
    'Resolve Respondent Filters Failure': props<{ error: string }>(),
    'Clear Respondent List Success': emptyProps(),
    'Clear Respondent List Failure': props<{ error: string }>(),
    // The survey's test entry link is in sync with the form — open it. No
    // payload: whoever emits this has already left the fresh survey in core
    // state, which is where the link is read from.
    'Test Entry Link Ready': emptyProps(),
    // A patch answers with a regenerated entry link but no full survey, so it
    // carries what the core reducer merges: the link, plus the URLs as the
    // form holds them (not as the server echoes them — the payload
    // substitutes the live URL for a blank test URL, and storing that echo
    // would make the next unchanged click look changed).
    'Patch Test Urls Success': props<{
      liveUrl: string;
      testUrl: string;
      testEntryLink: string;
    }>(),
    'Test Entry Link Failure': props<{ error: string }>(),
    // The basic token as the server holds it: the saved response on success,
    // or the previously saved one on failure — which reverts the form to it.
    'Basic Token Patched': props<{ basicToken: BasicToken }>(),
    'Aida Eligibility Loaded': props<{ eligibility: AidaEligibility }>(),
    'Aida Eligibility Failure': props<{ error: string }>(),
    'Launch Pricing Loaded': props<{ pricing: LaunchPricingSummary }>(),
    'Launch Pricing Failure': props<{ error: string }>(),
    // The save half of a launch succeeded — change the status next.
    'Change Launch Status': props<{
      surveyId: number;
      status: SurveyStatus;
      pricingConfirmationMessage: string;
    }>(),
    'Launch Survey Success': emptyProps(),
    'Launch Survey Failure': props<{ error: string }>(),
  },
});
